/**
 * EINE gemeinsame Quelle für GAEB-konforme OZ-Vergabe (F2, Ollis "1.2.1 statt 1-2-1").
 *
 * Strukturmodell (bewusste Entscheidung, siehe auch Kommentar in store.ts):
 * - Titel (Ebene 1)      = bestehende Gewerk-Gruppe (item.gewerk + gewerkOrder).
 * - Untertitel (Ebene 2) = Attribut item.untergewerkName; ein ZUSAMMENHÄNGENDER Lauf
 *   gleicher untergewerkName innerhalb eines Gewerks bildet die Untertitel-Gruppe.
 * - KEINE Header-Zeilen im positions-/quoteItems-Array: jede neue Zeilenart müsste von
 *   sämtlichen Summen-/Render-/Export-Konsumenten (quote-totals, pdfkit, docx, softec,
 *   invoice, mobile, pdf-lv, db-stats) einzeln gelernt werden — exakt die Builder-Falle
 *   aus F1. Attribute reisen dagegen durch jeden {...item}-Spread automatisch mit.
 *
 * OZ-Regeln (GAEB-üblich, ohne führende Nullen, PUNKTE statt Bindestriche):
 * - Titel: 1, 2, 3 … in gewerkOrder-Reihenfolge (unbekannte Gewerke danach in
 *   Erst-Auftritts-Reihenfolge).
 * - Innerhalb eines Titels zählt EIN Kind-Zähler in Anzeige-Reihenfolge:
 *   ein Item ohne Untertitel ist selbst das nächste Kind (1.1, 1.2 …);
 *   ein Untertitel-Lauf ist EIN Kind (z.B. 1.2) und nummeriert seine Items
 *   als Enkel (1.2.1, 1.2.2 …).
 *
 * Bestandsschutz (mengenBezug-Prinzip): diese Funktionen ÜBERSCHREIBEN nie von sich aus —
 * der Aufrufer entscheidet (expliziter Nutzer-Klick "Neu nummerieren" oder
 * numberingMode==='auto' nach diesem Klick). Importierte OZ (GAEB/PDF-LV) bleiben
 * unangetastet, solange der Nutzer die Neuvergabe nicht ausdrücklich startet.
 */

export type NumberingRow = {
  id: number;
  gewerk?: string | null;
  untergewerkName?: string | null;
};

export interface NumberedTitle {
  gewerk: string;
  oz: string;
}

export interface NumberedSubtitle {
  gewerk: string;
  untergewerkName: string;
  oz: string;
}

export interface QuoteNumbering {
  /** OZ je Zeilen-id, z.B. 7 → "1.2.1". */
  byId: Map<number, string>;
  titles: NumberedTitle[];
  subtitles: NumberedSubtitle[];
}

function normalizeGroupKey(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Berechnet die OZ für alle Zeilen (reine Funktion, keine Mutation).
 * rows MÜSSEN in Anzeige-Reihenfolge übergeben werden (= positions-Array-Reihenfolge).
 */
export function computeQuoteNumbering(
  rows: ReadonlyArray<NumberingRow>,
  gewerkOrder: ReadonlyArray<string> = [],
): QuoteNumbering {
  const byId = new Map<number, string>();
  const titles: NumberedTitle[] = [];
  const subtitles: NumberedSubtitle[] = [];

  // Gewerk-Gruppen in stabiler Reihenfolge: erst gewerkOrder, dann Erst-Auftritt.
  const groups = new Map<string, NumberingRow[]>();
  for (const row of rows || []) {
    const key = normalizeGroupKey(row?.gewerk);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(row);
  }

  const orderedKeys: string[] = [];
  for (const name of gewerkOrder || []) {
    const key = normalizeGroupKey(name);
    if (groups.has(key) && !orderedKeys.includes(key)) orderedKeys.push(key);
  }
  for (const key of groups.keys()) {
    if (!orderedKeys.includes(key)) orderedKeys.push(key);
  }

  let titleCounter = 0;
  for (const gewerkKey of orderedKeys) {
    const groupRows = groups.get(gewerkKey)!;
    if (groupRows.length === 0) continue; // Titel ohne Positionen: keine OZ-Lücke

    titleCounter += 1;
    const titleOz = String(titleCounter);
    titles.push({ gewerk: gewerkKey, oz: titleOz });

    let childCounter = 0;
    let index = 0;
    while (index < groupRows.length) {
      const row = groupRows[index];
      // getSubtitleName filtert untergewerkName===gewerk (Mapping-Metadatum) heraus.
      const subtitle = getSubtitleName(row);

      if (!subtitle) {
        // Direktes Kind unter dem Titel: 1.1, 1.2 …
        childCounter += 1;
        byId.set(row.id, `${titleOz}.${childCounter}`);
        index += 1;
        continue;
      }

      // Zusammenhängender Untertitel-Lauf = EIN Kind mit Enkel-Nummerierung.
      childCounter += 1;
      const subtitleOz = `${titleOz}.${childCounter}`;
      subtitles.push({ gewerk: gewerkKey, untergewerkName: subtitle, oz: subtitleOz });

      let grandChildCounter = 0;
      while (
        index < groupRows.length &&
        getSubtitleName(groupRows[index]) === subtitle
      ) {
        grandChildCounter += 1;
        byId.set(groupRows[index].id, `${subtitleOz}.${grandChildCounter}`);
        index += 1;
      }
    }
  }

  return { byId, titles, subtitles };
}

/** Normalisierter Untertitel-Name einer Zeile ('' = kein Untertitel).
 * WICHTIG: untergewerkName === gewerk ist Mapping-METADATUM (PDF-LV-Import/Softec
 * setzen es so), keine vom Nutzer angelegte Untergruppe — sonst bekäme jedes
 * importierte LV redundante Köpfe ("Elektro" gefolgt von "1.1 Elektro"). */
export function getSubtitleName(
  item: { untergewerkName?: string | null; gewerk?: string | null } | undefined,
): string {
  const raw = item?.untergewerkName;
  const name = typeof raw === 'string' ? raw.trim() : '';
  if (!name) return '';
  const gewerk = typeof item?.gewerk === 'string' ? item.gewerk.trim() : '';
  return name === gewerk ? '' : name;
}

/**
 * true, wenn an Index idx ein neuer Untertitel-Lauf beginnt (Renderer zeichnen dann
 * VOR diesem Item die Untertitel-Kopfzeile). Ein Lauf = zusammenhängende Folge
 * gleicher untergewerkName innerhalb der Anzeige-Reihenfolge — UND gleichen
 * OZ-Eltern-Präfixes: werden Zeilen herausgefiltert (z.B. Teil-Export), können zwei
 * gleichnamige Läufe verschiedener Eltern (1.1.x und 1.3.x) aneinanderstoßen; ohne
 * Präfix-Vergleich verschmölzen sie zu EINEM Kopf und 1.3.1 stünde unter "1.1".
 */
export function startsSubtitleRun(
  items: ReadonlyArray<{ untergewerkName?: string | null; positionNumber?: string | null } | undefined>,
  idx: number,
): boolean {
  const current = getSubtitleName(items[idx]);
  if (!current) return false;
  if (idx <= 0) return true;
  const previous = getSubtitleName(items[idx - 1]);
  if (current !== previous) return true;
  // Gleicher Name: nur derselbe Lauf, wenn auch das OZ-Eltern-Präfix übereinstimmt
  // (fehlende OZ auf einer Seite → kein Urteil möglich → als ein Lauf behandeln).
  const currentParent = getOzParentPrefix(items[idx]?.positionNumber);
  const previousParent = getOzParentPrefix(items[idx - 1]?.positionNumber);
  if (!currentParent || !previousParent) return false;
  return currentParent !== previousParent;
}

/** "1.2.1" → "1.2"; "1.1" → "1"; ohne Punkt → '' (kein Eltern-Präfix ableitbar). */
export function getOzParentPrefix(oz: unknown): string {
  if (typeof oz !== 'string') return '';
  const trimmed = oz.trim();
  const lastDot = trimmed.lastIndexOf('.');
  return lastDot > 0 ? trimmed.slice(0, lastDot) : '';
}

/**
 * Wendet die Nummerierung auf positions + quoteItems an (immutably) und bringt
 * quoteItems in die positions-Reihenfolge, damit Editor-, OZ- und Druckreihenfolge
 * übereinstimmen. EINZIGER Ort, der positionNumber neu vergibt.
 */
export function applyQuoteNumbering<
  P extends NumberingRow & { positionNumber?: string | null },
  Q extends { id: number; positionNumber?: string | null },
>(
  positions: ReadonlyArray<P>,
  quoteItems: ReadonlyArray<Q>,
  gewerkOrder: ReadonlyArray<string> = [],
): { positions: P[]; quoteItems: Q[]; numbering: QuoteNumbering } {
  const numbering = computeQuoteNumbering(positions, gewerkOrder);

  const nextPositions = positions.map((position) => {
    const oz = numbering.byId.get(position.id);
    return oz ? { ...position, positionNumber: oz } : { ...position };
  });

  const orderIndex = new Map<number, number>();
  positions.forEach((position, idx) => orderIndex.set(position.id, idx));

  const nextQuoteItems = quoteItems
    .map((item) => {
      const oz = numbering.byId.get(item.id);
      return oz ? { ...item, positionNumber: oz } : { ...item };
    })
    .sort((a, b) => (orderIndex.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (orderIndex.get(b.id) ?? Number.MAX_SAFE_INTEGER));

  return { positions: nextPositions, quoteItems: nextQuoteItems, numbering };
}
